import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { toast } from '../components/Toast'
import axios from 'axios'
import { buildApiUrl } from '../config/api'
import { AuthLogo } from '../components/Logo'

interface InviteData {
  name: string
  email: string
  organization: string
  profile: string
  profile_code: string
}

export default function RegisterPage() {
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')
  const [invite, setInvite] = useState<InviteData | null>(null)
  const [loadingInvite, setLoadingInvite] = useState(!!token)
  const [invalidToken, setInvalidToken] = useState(false)

  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [passwordConfirmation, setPasswordConfirmation] = useState('')
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const navigate = useNavigate()

  useEffect(() => {
    if (token) {
      axios.get(buildApiUrl(`/v1/auth/invite/${token}`))
        .then(res => {
          const data = res.data.data
          setInvite(data)
          setName(data.name)
          setEmail(data.email)
        })
        .catch(() => setInvalidToken(true))
        .finally(() => setLoadingInvite(false))
    }
  }, [token])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    const errs: Record<string, string> = {}
    if (!password) errs.password = 'Senha é obrigatória'
    else if (password.length < 6) errs.password = 'Mínimo 6 caracteres'
    if (password !== passwordConfirmation) errs.passwordConfirmation = 'Senhas não conferem'
    if (!token && !name) errs.name = 'Nome é obrigatório'
    if (!token && !email) errs.email = 'E-mail é obrigatório'
    setErrors(errs)
    if (Object.keys(errs).length > 0) return

    setLoading(true)
    try {
      if (token) {
        await axios.post(buildApiUrl(`/v1/auth/invite/${token}/accept`), {
          password,
          password_confirmation: passwordConfirmation,
        })
        toast('success', 'Conta criada com sucesso!', 'Faça login para acessar')
        navigate(`/login?email=${encodeURIComponent(invite.email)}`)
        return
      } else {
        await axios.post(buildApiUrl('/v1/auth/register'), { name, email, password, password_confirmation: passwordConfirmation })
        toast('success', 'Conta criada com sucesso!', 'Faça login para continuar')
      }
      navigate(`/login?email=${encodeURIComponent(email)}`)
    } catch (err: any) {
      toast('error', err.response?.data?.error || 'Erro ao criar conta')
    } finally {
      setLoading(false)
    }
  }

  if (loadingInvite) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <div className="w-8 h-8 border-4 border-brand-200 border-t-brand-500 rounded-full animate-spin mx-auto mb-3" />
        <p className="text-sm text-gray-500">Carregando convite...</p>
      </div>
    </div>
  )

  if (invalidToken) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-6">
      <div className="w-full max-w-sm text-center">
        <AuthLogo />
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Convite inválido</h1>
        <p className="text-gray-500 mb-6">Este convite expirou ou já foi utilizado.</p>
        <Link to="/login" className="text-brand-600 hover:text-brand-700 font-medium text-sm">Ir para o login</Link>
      </div>
    </div>
  )

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-6 py-12">
      <div className="w-full max-w-sm">
        <AuthLogo />

        {invite ? (
          <>
            <h1 className="text-2xl font-bold text-gray-900 mb-1 text-center">{invite.organization}</h1>
            <p className="text-gray-500 mb-8 text-center">Crie sua senha para acessar como <strong className="text-gray-700">{invite.profile}</strong></p>
          </>
        ) : (
          <>
            <h1 className="text-2xl font-bold text-gray-900 mb-1 text-center">Criar conta</h1>
            <p className="text-gray-500 mb-8 text-center">Preencha os dados para se cadastrar</p>
          </>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Nome completo</label>
            <input type="text" value={name} onChange={e => { setName(e.target.value); setErrors(er => ({ ...er, name: '' })) }}
              required placeholder="Seu nome" disabled={!!invite}
              className={`w-full px-4 py-2.5 border rounded-lg text-sm outline-none transition-colors ${invite ? 'bg-gray-50 text-gray-600 border-gray-200 cursor-not-allowed' : 'border-gray-300 focus:ring-2 focus:ring-brand-500 focus:border-brand-500'} ${errors.name ? 'border-error-300' : ''}`} />
            {errors.name && <p className="mt-1 text-sm text-error-500">{errors.name}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">E-mail</label>
            <input type="email" value={email} onChange={e => { setEmail(e.target.value); setErrors(er => ({ ...er, email: '' })) }}
              required placeholder="seu@email.com" disabled={!!invite}
              className={`w-full px-4 py-2.5 border rounded-lg text-sm outline-none transition-colors ${invite ? 'bg-gray-50 text-gray-600 border-gray-200 cursor-not-allowed' : 'border-gray-300 focus:ring-2 focus:ring-brand-500 focus:border-brand-500'} ${errors.email ? 'border-error-300' : ''}`} />
            {errors.email && <p className="mt-1 text-sm text-error-500">{errors.email}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Senha</label>
            <input type="password" value={password} onChange={e => { setPassword(e.target.value); setErrors(er => ({ ...er, password: '' })) }}
              required placeholder="Mínimo 6 caracteres"
              className={`w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none ${errors.password ? 'border-error-300' : ''}`} />
            {errors.password && <p className="mt-1 text-sm text-error-500">{errors.password}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Confirmar senha</label>
            <input type="password" value={passwordConfirmation} onChange={e => { setPasswordConfirmation(e.target.value); setErrors(er => ({ ...er, passwordConfirmation: '' })) }}
              required placeholder="Repita a senha"
              className={`w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none ${errors.passwordConfirmation ? 'border-error-300' : ''}`} />
            {errors.passwordConfirmation && <p className="mt-1 text-sm text-error-500">{errors.passwordConfirmation}</p>}
          </div>
          <button type="submit" disabled={loading}
            className="w-full py-2.5 bg-brand-600 hover:bg-brand-700 text-white font-medium rounded-lg text-sm transition-colors disabled:opacity-50">
            {loading ? 'Criando...' : 'Criar conta'}
          </button>
        </form>

        <p className="text-center text-sm text-gray-500 mt-6">
          Já tem uma conta? <Link to="/login" className="text-brand-600 hover:text-brand-700 font-medium">Entrar</Link>
        </p>
      </div>
    </div>
  )
}
